import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ListGrid from '../../../components/ListGrid';
import PageLoading from '../../../components/PageLoading';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import {
  loadConversions, buildFamilies, finalsOf, saveConversion, removeConversion,
  parseConversionSheet, planImport, applyImport,
} from './conversions';
import { readTable } from './morningReports';

/*
  PRODUCT CONVERSIONS, a tab of the Item Catalog. Ordering stage 3, 11 October 2026.

  **His answer of 11 October**: *"The product conversions will live in the item
  catalog."* He keeps this list; until now it was a sheet in his workbook.

  Each row is an old ClaimSoft number and what to buy instead, or "no
  replacement". **The family is worked out, never typed**: every number joined to
  any other by a conversion, in both directions, which is what the audit adds
  stock up over. **The end of the chain is worked out too**, so changing one row
  can never leave a "final replacement" column saying something stale.
*/

const blank = { old_number: '', old_vendor: '', old_description: '', new_number: '', new_vendor: '', new_description: '', notes: '' };

function Field({ label, value, onChange, placeholder, wide }) {
  return (
    <label style={{ display: 'block', gridColumn: wide ? '1 / -1' : undefined }}>
      <span className="fld-label">{label}</span>
      <input className="fld" style={{ width: '100%' }} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </label>
  );
}

function Editor({ row, rows, userId, onDone, onCancel }) {
  const toast = useToast();
  const [f, setF] = useState(row ? { ...blank, ...Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v ?? ''])) } : blank);
  const [none, setNone] = useState(row ? !row.new_number : false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const [removing, setRemoving] = useState(false);
  const set = k => v => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true); setProblem(null);
    const r = await saveConversion({ ...f, id: row && row.id, userId, new_number: none ? '' : f.new_number }, rows);
    setSaving(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    toast.success(row ? 'Conversion changed' : 'Conversion added');
    onDone();
  };
  const remove = async () => {
    setSaving(true); setProblem(null);
    const r = await removeConversion(row.id);
    setSaving(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    toast.success('Conversion removed');
    onDone();
  };

  return (
    <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
      <b>{row ? 'Change this conversion' : 'Add a conversion'}</b>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 10 }}>
        <Field label="Old item (do not use)" value={f.old_number} onChange={set('old_number')} placeholder="The ClaimSoft number" />
        <Field label="Its vendor" value={f.old_vendor} onChange={set('old_vendor')} />
        <Field label="Its description" value={f.old_description} onChange={set('old_description')} />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0 4px', fontSize: 13 }}>
        <input type="checkbox" checked={none} onChange={e => setNone(e.target.checked)} />
        No replacement: do not use it, and nothing replaces it
      </label>
      {!none && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 6 }}>
          <Field label="Replaced by" value={f.new_number} onChange={set('new_number')} placeholder="The ClaimSoft number" />
          <Field label="Its vendor" value={f.new_vendor} onChange={set('new_vendor')} />
          <Field label="Its description" value={f.new_description} onChange={set('new_description')} />
        </div>
      )}
      <div style={{ marginTop: 12 }}><Field label="Note (optional)" value={f.notes} onChange={set('notes')} wide /></div>
      {problem && <p style={{ color: 'var(--critical-ink)', margin: '10px 0 0' }}>{problem}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, justifyContent: 'flex-end' }}>
        {row && !removing && <button type="button" className="btn btn-quiet" style={{ marginRight: 'auto' }} disabled={saving} onClick={() => setRemoving(true)}>Remove this conversion</button>}
        {row && removing && (
          <span style={{ marginRight: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>Remove {row.old_number}{row.new_number ? ' to ' + row.new_number : ''}?</span>
            <button type="button" className="btn btn-quiet" disabled={saving} onClick={() => setRemoving(false)}>Keep it</button>
            <button type="button" className="btn btn-danger" disabled={saving} onClick={remove}>Yes, remove it</button>
          </span>
        )}
        <button type="button" className="btn btn-quiet" disabled={saving} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}

function ImportPreview({ plan, fileName, userId, onDone, onCancel }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const say = r => r.new_number ? r.new_number : 'no replacement';
  const go = async () => {
    setSaving(true); setProblem(null);
    const r = await applyImport(plan, userId, fileName);
    setSaving(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    toast.success(r.saved + ' conversion' + (r.saved === 1 ? '' : 's') + ' saved');
    onDone();
  };
  const nothing = !plan.add.length && !plan.change.length;
  return (
    <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
      <b>{fileName}</b>
      <p style={{ margin: '6px 0' }}>
        {plan.add.length} new, {plan.change.length} changed, {plan.same} already the same.
        {' '}Nothing in OnHand that this file leaves out is removed.
      </p>
      {plan.change.length > 0 && (
        <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13 }}>
          {plan.change.map(c => (
            <li key={c.old_number}><b>{c.old_number}</b>: {c.from.map(say).join(' and ')} becomes {c.to.map(say).join(' and ')}</li>
          ))}
        </ul>
      )}
      {plan.problems.map(p => <p key={p} style={{ color: 'var(--warning-ink)', margin: '4px 0', fontSize: 13 }}>{p}</p>)}
      {problem && <p style={{ color: 'var(--critical-ink)' }}>{problem}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button type="button" className="btn btn-quiet" disabled={saving} onClick={onCancel}>{nothing ? 'Close' : 'Cancel'}</button>
        {!nothing && <button type="button" className="btn btn-primary" disabled={saving} onClick={go}>{saving ? 'Saving...' : 'Bring them in'}</button>}
      </div>
    </div>
  );
}

export default function ConversionsTab({ readOnly = false }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);     // a row, or 'new'
  const [importing, setImporting] = useState(null); // { plan, fileName } or { problem, fileName }
  const fileRef = useRef(null);

  const read = useCallback(async () => {
    const r = await loadConversions();
    if (r.error) { setFailed(r.error.message); setRows([]); return; }
    setFailed(null);
    setRows(r.rows);
  }, []);
  useEffect(() => { read(); }, [read]);

  const fam = useMemo(() => buildFamilies(rows || []), [rows]);
  const realFamilies = useMemo(() => [...fam.members.values()].filter(m => m.length > 1).length, [fam]);
  const twoWays = useMemo(() => [...fam.byOld].filter(([, rs]) => rs.length > 1).map(([k]) => fam.spell.get(k)), [fam]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!rows) return [];
    if (!q) return rows;
    return rows.filter(r => [r.old_number, r.new_number, r.old_vendor, r.new_vendor, r.old_description, r.new_description]
      .some(v => (v || '').toLowerCase().includes(q))
      || fam.members.get(fam.familyOf.get(r.old_key)).some(k => k.toLowerCase().includes(q)));
  }, [rows, search, fam]);

  const bringIn = async file => {
    if (!file) return;
    try {
      const { table } = await readTable(file);
      let parsed = parseConversionSheet(table);
      /* His workbook keeps the list on a sheet of its own, not the first one. */
      if (parsed.problem && /\.(xlsx|xlsm|xls)$/i.test(file.name)) {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
        const name = wb.SheetNames.find(n => /product conversions/i.test(n));
        if (name) parsed = parseConversionSheet(XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', raw: false }));
      }
      if (parsed.problem) { setImporting({ fileName: file.name, problem: parsed.problem }); return; }
      setImporting({ fileName: file.name, plan: planImport(parsed.rows, rows || []) });
    } catch (e) {
      setImporting({ fileName: file.name, problem: 'That file could not be read: ' + (e && e.message ? e.message : String(e)) });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (rows === null) return <PageLoading rows={8} />;
  const done = () => { setEditing(null); setImporting(null); read(); };
  const noRep = rows.filter(r => !r.new_number).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="tb-search" placeholder="Search a number, a vendor or a description" value={search}
          onChange={e => setSearch(e.target.value)} aria-label="Search the conversions" />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {rows.length} conversion{rows.length === 1 ? '' : 's'}, {realFamilies} famil{realFamilies === 1 ? 'y' : 'ies'} of two or more numbers, {noRep} with no replacement
        </span>
        <span style={{ flex: 1 }} />
        {!readOnly && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.txt" style={{ display: 'none' }}
              aria-label="Bring in a conversions list" onChange={e => bringIn(e.target.files && e.target.files[0])} />
            <button type="button" className="btn btn-quiet" onClick={() => fileRef.current && fileRef.current.click()}>Bring in a list</button>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>+ Add a conversion</button>
          </>
        )}
      </div>

      {failed && <p style={{ color: 'var(--critical-ink)' }}>OnHand could not read the conversions just now: {failed}</p>}
      {twoWays.length > 0 && (
        <p style={{ fontSize: 13, color: 'var(--warning-ink)', fontWeight: 700 }}>
          Replaced by more than one item: {twoWays.join(', ')}. The audit counts every one of them as the same family.
        </p>
      )}
      {importing && importing.problem && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
          <b>{importing.fileName}</b>
          <p style={{ color: 'var(--critical-ink)', margin: '6px 0' }}>{importing.problem}</p>
          <button type="button" className="btn btn-quiet" onClick={() => setImporting(null)}>Close</button>
        </div>
      )}
      {importing && importing.plan && (
        <ImportPreview plan={importing.plan} fileName={importing.fileName} userId={user && user.id}
          onDone={done} onCancel={() => setImporting(null)} />
      )}
      {editing && (
        <Editor key={editing === 'new' ? 'new' : editing.id} row={editing === 'new' ? null : editing} rows={rows}
          userId={user && user.id} onDone={done} onCancel={() => setEditing(null)} />
      )}

      <ListGrid
        layoutKey="product_conversions"
        layoutTitle="The conversions' columns"
        columns={[
          { label: 'Old item, do not use', width: 'minmax(200px, 1.4fr)' },
          { label: 'Replaced by', width: 'minmax(200px, 1.4fr)' },
          { label: 'End of the chain', width: 'minmax(120px, 0.8fr)' },
          { label: 'Family', width: 'minmax(180px, 1.2fr)' },
        ]}
        rows={shown}
        keyOf={r => r.id}
        onRowClick={readOnly ? undefined : r => setEditing(r)}
        empty={rows.length ? 'No conversion matches that.' : 'No conversions yet. Add one, or bring in your list.'}
        cells={r => {
          const finals = finalsOf(fam, r.old_key).map(k => fam.spell.get(k));
          const family = fam.members.get(fam.familyOf.get(r.old_key)).map(k => fam.spell.get(k));
          return [
            <span>
              <span className="rpnum">{r.old_number}</span>
              <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                {[r.old_vendor, r.old_description].filter(Boolean).join(' · ')}
              </span>
            </span>,
            r.new_number ? (
              <span>
                <span className="rpnum">{r.new_number}</span>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {[r.new_vendor, r.new_description].filter(Boolean).join(' · ')}
                </span>
              </span>
            ) : <span style={{ color: 'var(--critical-ink)', fontWeight: 700 }}>No replacement</span>,
            finals.length ? <span className="rpnum">{finals.join(', ')}</span> : '',
            <span style={{ fontSize: 13 }}>{family.length > 1 ? family.join(', ') : ''}</span>,
          ];
        }}
      />
    </div>
  );
}

